import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { Field, Modal, useToast } from "@cx/ui";
import {
  PRESENCE_INDICATORS,
  TAX_DOC_MODELS,
  TAX_OPERATION_PURPOSES,
  type TaxOperationInput,
  type TaxOperationDetail,
  type TaxOperationView,
} from "@cx/contracts";
import type { TaxDirection } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { DIRECTION_LABEL, DOC_MODEL_LABEL, PRESENCE_LABEL, PURPOSE_LABEL } from "./tax-labels";

type Values = Omit<TaxOperationInput, "financialCategoryId"> & { financialCategoryId: string | null };

const EMPTY: Values = {
  name: "",
  direction: "out",
  purpose: 1,
  docModel: "55",
  series: 1,
  presenceIndicator: 1,
  postStock: true,
  postFinancial: true,
  financialCategoryId: null,
  additionalInfo: null,
  fiscoInfo: null,
  isDefault: false,
  active: true,
};

const fromView = (o: TaxOperationView): Values => ({
  name: o.name,
  direction: o.direction,
  purpose: o.purpose,
  docModel: o.docModel,
  series: o.series,
  presenceIndicator: o.presenceIndicator,
  postStock: o.postStock,
  postFinancial: o.postFinancial,
  financialCategoryId: o.financialCategoryId,
  additionalInfo: o.additionalInfo,
  fiscoInfo: o.fiscoInfo,
  isDefault: o.isDefault,
  active: o.active,
});

/** Inclusão e edição dos dados da natureza (as regras ficam na tela da natureza). */
export function TaxOperationDialog({
  operation,
  onClose,
  onSaved,
}: {
  operation?: TaxOperationDetail;
  onClose(): void;
  onSaved(id: string): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [v, setV] = useState<Values>(() => (operation ? fromView(operation) : EMPTY));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof Values>(k: K, value: Values[K]) => setV((x) => ({ ...x, [k]: value }));
  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(v);
      if (operation) {
        await api(`/tax-operations/${operation.id}`, { method: "PUT", body });
        return operation.id;
      }
      return (await api<{ id: string }>("/tax-operations", { method: "POST", body })).id;
    },
    onSuccess: (id) => {
      toast("success", "Natureza de operação salva");
      void qc.invalidateQueries({ queryKey: ["tax-operations", companyId] });
      setOpen(false);
      onSaved(id);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_tax_operation")
        setErrors({ name: e.message });
      toast("danger", errorMessage(e));
    },
  });
  const hasRules = (operation?.rules?.length ?? 0) > 0;
  return (
    <Modal
      open={open}
      title={operation ? "Editar natureza de operação" : "Nova natureza de operação"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div
        className="row"
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input
            value={v.name}
            maxLength={120}
            data-autofocus
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field
          label="Tipo"
          required
          className="col-md-6"
          error={errors.direction}
          hint={hasRules ? "Com regras cadastradas o tipo não muda." : undefined}
        >
          <select
            className="form-select"
            value={v.direction}
            disabled={hasRules}
            onChange={(e) => set("direction", e.target.value as TaxDirection)}
          >
            {(["out", "in"] as const).map((d) => (
              <option key={d} value={d}>
                {DIRECTION_LABEL[d]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Finalidade" className="col-md-6" error={errors.purpose}>
          <select
            className="form-select"
            value={v.purpose}
            onChange={(e) => set("purpose", Number(e.target.value) as Values["purpose"])}
          >
            {TAX_OPERATION_PURPOSES.map((p) => (
              <option key={p} value={p}>
                {PURPOSE_LABEL[p]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Modelo do documento" className="col-md-6" error={errors.docModel}>
          <select
            className="form-select"
            value={v.docModel}
            onChange={(e) => set("docModel", e.target.value as Values["docModel"])}
          >
            {TAX_DOC_MODELS.map((m) => (
              <option key={m} value={m}>
                {DOC_MODEL_LABEL[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Presença do comprador" className="col-md-6" error={errors.presenceIndicator}>
          <select
            className="form-select"
            value={v.presenceIndicator}
            onChange={(e) => set("presenceIndicator", Number(e.target.value) as Values["presenceIndicator"])}
          >
            {PRESENCE_INDICATORS.map((p) => (
              <option key={p} value={p}>
                {PRESENCE_LABEL[p]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Informações complementares"
          className="col-12"
          error={errors.additionalInfo}
          hint="Vão na nota."
        >
          <textarea
            rows={2}
            maxLength={2000}
            value={v.additionalInfo ?? ""}
            onChange={(e) => set("additionalInfo", e.target.value || null)}
          />
        </Field>
        <div className="col-12">
          {(
            [
              ["postStock", "Movimenta o estoque"],
              ["postFinancial", "Gera contas a receber/pagar"],
              ["isDefault", `Natureza padrão de ${v.direction === "out" ? "saída" : "entrada"}`],
              ["active", "Ativa"],
            ] as const
          ).map(([k, label]) => (
            <div className="form-check" key={k}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`tax-op-${k}`}
                checked={v[k]}
                onChange={(e) => set(k, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`tax-op-${k}`}>
                {label}
              </label>
            </div>
          ))}
          {errors.isDefault && <div className="invalid-feedback d-block">{errors.isDefault}</div>}
        </div>
      </div>
    </Modal>
  );
}
